import { useEffect, useRef, useState } from "react";
import {
  actionStatusColors,
  isBrightColor,
  mailStatuses,
  sliceLabelName,
} from "../helpers";
import { useContent } from "contexts/contentContext/contentContext";
import Icon, { IconWithTooltip } from "components/Elements/Icon/Icon";
import useInbox from "hooks/useInbox.ts";
import { pickerStatusColors } from "../Picker/models/enums";
import { useAppSelector } from "../../../../store/hooks";
import {
  selectPickerSearchParams,
  selectSearchParams,
  selectTParam,
} from "../../../../store/selectors/inboxSelectors";

const DARK_FONT_COLOR = "#3F4648";
const LIGHT_FONT_COLOR = "#FFFFFF";
const ANIMATION_DURATION_MS = 300;

const StatusFilterBar = () => {
  const searchParams = useAppSelector(selectSearchParams);
  const pickerSearchParams = useAppSelector(selectPickerSearchParams);
  const pageType = useAppSelector(selectTParam);
  const { getTextByKey } = useContent();
  const { getLabels, setSearchParams, setPickerSearchParams, setPageNumber } = useInbox();
  const ulRef = useRef<HTMLUListElement>(null);
  const [showArrows, setShowArrows] = useState(false);

  useEffect(() => {
    if (ulRef.current) {
      setShowArrows(
        document.body?.clientWidth * 0.3 < ulRef.current?.clientWidth
      );
    }
  }, [ulRef.current?.clientWidth]);

  const handleRemove = (
    index: number,
    status: string,
    type: "label" | "status" | "action"
  ) => {
    // set exit animation
    ulRef.current?.children[index].classList.add("li-exit");

    // remove after animation finished
    setTimeout(() => {
      switch (type) {
        case "status": {
          setSearchParams({
            sm: searchParams.sm?.filter((value) => value !== status),
          });
          break;
        }
        case "label": {
          setSearchParams({
            l: searchParams.l?.filter((value) => value !== status),
          });
          break;
        }
        case "action": {
          setSearchParams({
            sa: searchParams.sa?.filter((value) => value !== status),
          });
        }
      }
      setPageNumber(1)
    }, ANIMATION_DURATION_MS);
  };

  const handleClearAll = () => {
    setShowArrows(false);

    if (pageType === "picker") {
      setPickerSearchParams({
        ps: [],
      });
    } else {
      setSearchParams({
        sm: [],
        sa: [],
        l: [],
      });
    }
    setPageNumber(1);
  };

  const handleClickArrow = (
    _: React.MouseEvent<HTMLDivElement, MouseEvent>,
    dir: "left" | "right"
  ) => {
    const itemCount =
      pageType === "picker"
        ? pickerSearchParams.ps.length
        : searchParams.sm!.length +
          searchParams.l!.length +
          searchParams.sa!.length;
    ulRef.current?.scrollBy({
      left:
        dir === "left"
          ? -(ulRef.current?.clientWidth / (itemCount / 2))
          : ulRef.current?.clientWidth / (itemCount / 2),
      behavior: "smooth",
    });
  };

  const handleRemovePickerStatus = (index: number, status: string) => {
    // set exit animation
    ulRef.current?.children[index].classList.add("li-exit");

    setTimeout(() => {
      setPickerSearchParams({
        ps: pickerSearchParams.ps?.filter((v) => status !== v),
      });
      setPageNumber(1);
    }, ANIMATION_DURATION_MS);
  };

  return (
    <div className="status-filter-bar-wrapper">
      {showArrows && (
        <div
          className="arrow-icon-wrapper"
          onClick={(e: any) => handleClickArrow(e, "left")}
        >
          <Icon name={"arrow-left"} gray small />
        </div>
      )}
      {(!!searchParams?.sm?.length ||
        !!searchParams?.sa?.length ||
        !!searchParams?.l?.length ||
        !!pickerSearchParams?.ps?.length) && (
        <ul ref={ulRef}>
          {pageType !== "picker" &&
            searchParams.sm?.map((status, i) => (
              <li key={status} style={{ animationDelay: `${i / 10}s` }}>
                {getTextByKey((mailStatuses as any)[status]?.name)}
                <IconWithTooltip
                  iconProps={{
                    name: "x",
                    onClick: () => handleRemove(i, status, "status"),
                    gray: true,
                    iconClass: "remove-icon",
                  }}
                  tooltipProps={{
                    tooltipTextKey: "remove-filter-status",
                  }}
                />
              </li>
            ))}
          {pageType !== "picker" &&
            searchParams?.sa?.map((status, i) => (
              <li
                key={status}
                style={{
                  animationDelay: `${i / 10}s`,
                  color: (actionStatusColors as any)[status],
                  border: `1px solid ${(actionStatusColors as any)[status]}`,
                }}
              >
                {getTextByKey(status)}
                <IconWithTooltip
                  iconProps={{
                    name: "x",
                    onClick: () =>
                      handleRemove(
                        i + (searchParams.sm?.length || 0),
                        status,
                        "action"
                      ),
                    gray: true,
                    iconClass: "remove-icon",
                  }}
                  tooltipProps={{
                    tooltipTextKey: "remove-filter-status",
                  }}
                />
              </li>
            ))}
          {pageType !== "picker" &&
            searchParams.l?.map((label, i) => (
              <li
                key={label}
                style={{
                  animationDelay: `${i / 10}s`,
                  backgroundColor: getLabels()[label]?.color,
                  color: isBrightColor(getLabels()[label]?.color)
                    ? DARK_FONT_COLOR
                    : LIGHT_FONT_COLOR,
                  border: "none",
                }}
              >
                {sliceLabelName(getLabels()[label]?.name)}
                <IconWithTooltip
                  iconProps={{
                    name: "x",
                    onClick: () =>
                      handleRemove(
                        i +
                          (searchParams.sm?.length || 0) +
                          +(searchParams.sa?.length || 0),
                        label,
                        "label"
                      ),
                    darkGray: isBrightColor(getLabels()[label]?.color),
                    white: !isBrightColor(getLabels()[label]?.color),
                    iconClass: "remove-icon",
                  }}
                  tooltipProps={{
                    tooltipTextKey: `remove-filter-label`,
                  }}
                />
              </li>
            ))}
          {pageType === "picker" &&
            pickerSearchParams.ps.map((status, i) => (
              <li
                key={status}
                style={{
                  animationDelay: `${i / 10}s`,
                  border: `1px solid ${pickerStatusColors[status]}`,
                  color: pickerStatusColors[status],
                  backgroundColor: "#fff",
                }}
              >
                {getTextByKey(status + "-picker-status")}
                <IconWithTooltip
                  iconProps={{
                    name: "x",
                    onClick: () => handleRemovePickerStatus(i, status),
                    iconClass: "remove-icon",
                    //@ts-ignore
                    style: {
                      opacity: 0.4,
                    },
                  }}
                  tooltipProps={{
                    tooltipTextKey: `remove-filter-status`,
                  }}
                />
              </li>
            ))}
        </ul>
      )}
      {showArrows && (
        <div
          className="arrow-icon-wrapper"
          onClick={(e: any) => handleClickArrow(e, "right")}
        >
          <Icon name={"arrow-right"} gray small />
        </div>
      )}
      {(((!!searchParams?.sm?.length || !!searchParams?.l?.length) &&
        pageType !== "picker") ||
        !!pickerSearchParams?.ps.length) && (
        <button onClick={handleClearAll}>
          {getTextByKey("filter-clear-all")}
        </button>
      )}
    </div>
  );
};

export default StatusFilterBar;
